import React, { useState, useEffect } from "react"
import Button from "src/components/tailwind-components/Button"
import { useGetAutoAcquisitionAccounts } from "../hooks/useGetAutoAcquisitionAccounts"
import { useUpdateAutoAcquisitionAccountPassword } from "../hooks/useUpdateAutoAcquisitionAccountPassword"
import { AccountTypeLabels, AutoAcquisitionAccount } from "../types"
import { useNotification } from "@smoothincorg/smooth-ui"

interface AccountManagementModalProps {
  onClose: () => void
}

export const AccountManagementModal: React.FC<AccountManagementModalProps> = ({ onClose }) => {
  const { autoAcquisitionAccounts, isLoading, error, refresh } = useGetAutoAcquisitionAccounts()
  const { updateAccount, isUpdating } = useUpdateAutoAcquisitionAccountPassword()
  const { showSnackbar } = useNotification()
  const [editingAccountId, setEditingAccountId] = useState<number | null>(null)
  const [newPassword, setNewPassword] = useState("")
  const [newName, setNewName] = useState("")

  // モーダルが開かれた時に最新データを取得
  useEffect(() => {
    refresh()
  }, [])

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleEditClick = (account: AutoAcquisitionAccount) => {
    setEditingAccountId(account.id)
    setNewPassword("")
    setNewName(account.name || "")
  }

  const handleCancelEdit = () => {
    setEditingAccountId(null)
    setNewPassword("")
    setNewName("")
  }

  const handleSaveAccount = async (accountId: number) => {
    // 名前もパスワードも入力されていない場合はエラー
    if (!newPassword.trim() && !newName.trim()) {
      showSnackbar("名前またはパスワードのいずれかを入力してください", "warning")
      return
    }

    try {
      // パスワードが入力されている場合のみパスワードを更新、名前は常に送信
      await updateAccount(
        accountId,
        newPassword.trim() ? newPassword : undefined,
        newName.trim() ? newName : undefined
      )
      showSnackbar("アカウント情報を更新しました", "success")
      setEditingAccountId(null)
      setNewPassword("")
      setNewName("")
      refresh()
    } catch (error) {
      showSnackbar("アカウント情報の更新に失敗しました", "warning")
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackgroundClick}
    >
      <div
        className="relative w-full max-w-6xl rounded-lg bg-white p-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="mb-6 flex items-center justify-between border-b pb-4">
          <h2 className="text-2xl font-bold text-gray-900">アカウント管理</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="閉じる"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* コンテンツ */}
        <div className="mb-6">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">読み込み中...</div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-red-500">データの読み込みに失敗しました</div>
            </div>
          )}

          {!isLoading && !error && autoAcquisitionAccounts.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">アカウントが登録されていません</div>
            </div>
          )}

          {!isLoading && !error && autoAcquisitionAccounts.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full table-fixed divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-[100px] px-4 py-3 text-center align-middle text-[14px] font-medium uppercase tracking-wider text-gray-500">
                      ID
                    </th>
                    <th className="w-auto px-4 py-3 text-left align-middle text-[14px] font-medium uppercase tracking-wider text-gray-500">
                      ログインID
                    </th>
                    <th className="w-[200px] px-4 py-3 text-left align-middle text-[14px] font-medium uppercase tracking-wider text-gray-500">
                      名前
                    </th>
                    <th className="w-[200px] px-4 py-3 text-center align-middle text-[14px] font-medium uppercase tracking-wider text-gray-500">
                      種別
                    </th>
                    <th className="w-[150px] px-4 py-3 text-center align-middle text-[14px] font-medium uppercase tracking-wider text-gray-500">
                      {/* 編集ボタン用 */}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {autoAcquisitionAccounts.map((account) => (
                    <tr
                      key={account.id}
                      className="border-b border-gray-200 transition-colors hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 text-center align-middle text-[14px] font-bold text-gray-900">
                        {account.id}
                      </td>
                      <td className="px-4 py-3 align-middle text-[14px] font-bold text-gray-900">
                        {account.loginId}
                      </td>
                      <td className="px-4 py-3 align-middle text-[14px] font-bold text-gray-900">
                        {account.name || "-"}
                      </td>
                      <td className="px-4 py-3 text-center align-middle text-[14px] font-bold text-gray-900">
                        {AccountTypeLabels[account.accountType]}
                      </td>
                      <td className="px-4 py-3 text-right align-middle">
                        <Button
                          type="button"
                          mode="primary"
                          label="編集"
                          className="min-w-[6.4rem]"
                          onClick={() => handleEditClick(account)}
                          disabled={isUpdating}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* フッター */}
        <div className="flex justify-end border-t pt-4">
          <Button type="button" mode="secondary" label="閉じる" onClick={onClose} />
        </div>
      </div>

      {/* パスワード編集モーダル */}
      {editingAccountId !== null &&
        (() => {
          const editingAccount = autoAcquisitionAccounts.find((acc) => acc.id === editingAccountId)
          return (
            <div
              className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50"
              onClick={handleCancelEdit}
            >
              <div
                className="relative w-full max-w-4xl rounded-lg bg-white p-10 shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-8 flex items-center justify-between border-b pb-6">
                  <h3 className="text-3xl font-bold text-gray-900">アカウント編集</h3>
                  <button
                    onClick={handleCancelEdit}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="閉じる"
                  >
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>

                {/* アカウント情報表示 */}
                {editingAccount && (
                  <div className="mb-8 rounded-md bg-blue-50 border-2 border-blue-200 p-8">
                    <div className="mb-6">
                      <div className="text-xl font-medium text-gray-700 mb-2">名前</div>
                      <div className="text-4xl font-bold text-blue-900 break-all">
                        {editingAccount.name || "-"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xl font-medium text-gray-700 mb-2">
                        ログインID（種別）
                      </div>
                      <div className="text-2xl font-bold text-gray-900">
                        {editingAccount.loginId} （{AccountTypeLabels[editingAccount.accountType]}）
                      </div>
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  <label className="mb-3 block text-lg font-medium text-gray-700">名前</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-6 py-4 text-lg focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="名前を入力"
                  />
                </div>

                <div className="mb-8">
                  <label className="mb-3 block text-lg font-medium text-gray-700">
                    新しいパスワード（任意）
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-6 py-4 text-lg focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="パスワードを変更する場合のみ入力"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    ※ パスワードを変更しない場合は空欄のままにしてください
                  </p>
                </div>

                <div className="flex justify-end gap-4">
                  <Button
                    type="button"
                    mode="secondary"
                    label="キャンセル"
                    onClick={handleCancelEdit}
                    disabled={isUpdating}
                  />
                  <Button
                    type="button"
                    mode="primary"
                    label="保存"
                    onClick={() => handleSaveAccount(editingAccountId)}
                    disabled={isUpdating || (!newPassword.trim() && !newName.trim())}
                  />
                </div>
              </div>
            </div>
          )
        })()}
    </div>
  )
}
